<template>
  <div>
    <Modal modalId="manage-plan-modal" size="modal-md">
      <ModalHeader :title="$t('xManagePlan')" icon="far fa-circle-dollar colorOrange mr-1">
        <div class="cols-auto">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeManagePlanModal()"
            data-test-hook="close-manage-plan-modal"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="custom-control custom-radio mt-2 mb-3">
          <input
            class="custom-control-input"
            type="radio"
            id="changePaymentCadence"
            v-model="managePlanType"
            value="changePaymentCadence"
            data-test-hook="radio-change-payment-cadence"
          />
          <label class="custom-control-label" for="changePaymentCadence">
            {{ $t('xChangePaymentCadence') }}
          </label>
          <p class="text-small radio-button-subtext ">
            {{ $t('xChangePaymentCadenceSubtext') }}
          </p>
        </div>
        <div v-if="shouldShowRemoveAndDowngradeOptions()" class="custom-control custom-radio mb-3">
          <input
            class="custom-control-input"
            type="radio"
            id="removeProducts"
            v-model="managePlanType"
            value="removeProducts"
            data-test-hook="radio-remove-products"
          />
          <label class="custom-control-label" for="removeProducts">
            {{ $t('xRemoveProducts') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xRemoveProductsSubtext') }}
          </p>
        </div>
        <div v-if="shouldShowRemoveAndDowngradeOptions()" class="custom-control custom-radio mb-3">
          <input
            class="custom-control-input"
            type="radio"
            id="downgradePlan"
            v-model="managePlanType"
            value="downgradePlan"
            data-test-hook="radio-downgrade-plan"
          />
          <label class="custom-control-label" for="downgradePlan">
            {{ $t('xDowngradePlan') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xDowngradePlanSubtext') }}
          </p>
        </div>
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            type="radio"
            id="cancelPlan"
            v-model="managePlanType"
            value="cancelPlan"
            data-test-hook="radio-cancel-plan"
          />
          <label class="custom-control-label" for="cancelPlan">
            {{ $t('xCancelPlanLowercase') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xCancelPlanSubtext') }}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          class="btn btn-outline-warning flex flex-grow-1"
          @click="closeManagePlanModal()"
          data-test-hook="cancel-button-manage-plan-modal"
        >
          {{ $t('xCancel') }}
        </button>
        <button
          class="btn btn-warning flex flex-grow-1"
          :disabled="!managePlanType"
          @click="goToSelectedOption()"
          data-test-hook="next-button-manage-plan-modal"
        >
          {{ $t('xNext') }}
          <span class="pl-1">
            <i class="far fa-arrow-right"></i>
          </span>
        </button>
      </ModalFooter>
    </Modal>

    <PaymentCadenceModal
      :endDate="subscription.subscriptionRenewalDate"
      :oldSubscriptionProductId="
        subscription.subscriptionProductId !== undefined ? subscription.subscriptionProductId : null
      "
      :subscriptionStore="subscription.subscriptionStore !== undefined ? subscription.subscriptionStore : null"
    />

    <RemoveProductsSelectionModal :subscription="subscription" :account-profile="accountProfile" />

    <CancelSubscriptionModal
      :isCancelLegacy="false"
      :endDate="subscription.subscriptionRenewalDate"
      :cancelLegacyApparatusId="null"
      :cancelLegacyApparatusType="null"
      :cancelSubscriptionApparatuses="subscription.apparatuses !== null ? subscription.apparatuses : []"
      :cancelSubscriptionProductId="
        subscription.subscriptionProductId !== undefined ? subscription.subscriptionProductId : null
      "
      :cancelSubscriptionStore="subscription.subscriptionStore !== undefined ? subscription.subscriptionStore : null"
    />

    <DowngradeSubscriptionModal
      :currentSubscription="subscription"
      :newSubscription="newSubscription"
      @openPayment="proceedToPayment"
    />

    <DowngradeUnavailableModal
      :account-profile="accountProfile"
      @mustRemoveBeforeDowngrading="mustRemoveBeforeDowngrading"
    />

    <PaymentModal
      :modal-id="'new-subscription-payment'"
      :selected-product="newSubscription"
      :account-profile="accountProfile"
      @submit="subscribeProduct"
      @goBack="returnToDowngrade"
      @savePremiumFeaturesGettingReadyInfo="savePremiumFeaturesGettingReadyInfo"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as Constants from '@/constants'
import {
  isDealerManaged,
  planEligibleForDowngrade,
  isHomePremiumSubscription,
  canDowngradeToSingleHomePlan,
  canDowngradeToSingleGeneratorPlan,
  canDowngradeToSingleFuelMonitorPlan,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive
} from '@/utils/mixins'
import CancelSubscriptionModal from './CancelSubscriptionModal.vue'
import PaymentCadenceModal from './PaymentCadenceModal.vue'
import DowngradeSubscriptionModal from './DowngradeSubscriptionModal.vue'
import PaymentModal from '@/components/Enrollment/Subscription/PaymentModal.vue'
import DowngradeUnavailableModal from './DowngradeUnavailableModal.vue'
import RemoveProductsSelectionModal from './RemoveProductsSelectionModal.vue'

const ManagePlanTypes = {
  CHANGE_PAYMENT_CADENCE: 'changePaymentCadence',
  REMOVE_PRODUCTS: 'removeProducts',
  DOWNGRADE_PLAN: 'downgradePlan',
  CANCEL_PLAN: 'cancelPlan'
}

export default {
  components: {
    CancelSubscriptionModal,
    PaymentCadenceModal,
    DowngradeSubscriptionModal,
    DowngradeUnavailableModal,
    PaymentModal,
    RemoveProductsSelectionModal
  },
  props: ['subscription'],
  data() {
    return {
      managePlanType: ManagePlanTypes.CHANGE_PAYMENT_CADENCE,
      newSubscription: null
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      apparatuses: state => state.apparatus.apparatuses
    })
  },
  methods: {
    async savePremiumFeaturesGettingReadyInfo() {
      var subscriptionDetails = {}
      await this.$store.dispatch('getSubscriptionDetails', this.accountProfile.organizationId).then(response => {
        subscriptionDetails = response.data.subscriptions.find(
          sub =>
            subscriptionDetailActive(sub.status) &&
            sub.apparatuses?.length > 0 &&
            subscriptionDetailContainsApparatus(sub.apparatuses, this.subscription.apparatuses[0].id) //since they have to upgrade all apparatus, we only need to check for one apparatus
        )
      })
      // if processing is complete, show toast
      if (
        subscriptionDetails &&
        (subscriptionDetails?.product.productId === this.newSubscription.productId ||
          subscriptionDetails?.status === Constants.SubscriptionDetailPendingProductChange)
      ) {
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xPlanChangedSuccessfully'),
          variant: 'success'
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        // processing not complete, add each apparatus data to state to trigger notification banner
        await this.apparatuses.forEach(apparatus => {
          this.$store.dispatch('addPremiumFeaturesGettingReadyInfo', {
            apparatusId: apparatus.apparatusId,
            productSubscribedTo: this.newSubscription.productId,
            timeStamp: Date.now()
          })
        })
        window.location.reload()
      }
    },
    closeManagePlanModal() {
      this.closeModal('manage-plan-modal')
    },
    mustRemoveBeforeDowngrading() {
      this.openModal('manage-plan-modal')
      this.managePlanType = ManagePlanTypes.REMOVE_PRODUCTS
    },
    shouldShowRemoveAndDowngradeOptions() {
      return isHomePremiumSubscription(this.subscription?.subscriptionProductId)
    },
    async openDowngradeModal() {
      let getPlan = null
      const response = await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .catch(() => {})
      getPlan = response.data?.subscriptions.find(
        subscription => subscription?.product.productId === this.subscription?.subscriptionProductId
      )
      if (this.planEligibleForDowngrade(getPlan)) {
        await this.$store.dispatch('getAllWebSubscriptionProducts').then(response => {
          if (this.canDowngradeToSingleHomePlan(getPlan)) {
            if (getPlan?.product.durationInMonths === 12) {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.HomePremiumOneYearlyProductId
              )
            } else {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.HomePremiumOneMonthlyProductId
              )
            }
          } else if (this.canDowngradeToSingleGeneratorPlan(getPlan)) {
            if (getPlan?.product.durationInMonths === 12) {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.SingleGeneratorYearlyProductId
              )
            } else {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.SingleGeneratorMonthlyProductId
              )
            }
          } else if (this.canDowngradeToSingleFuelMonitorPlan(getPlan)) {
            if (getPlan?.product.durationInMonths === 12) {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.SingleFuelMonitorYearlyProductId
              )
            } else {
              this.newSubscription = response.data?.products.find(
                product => product.productId === Constants.SingleFuelMonitorMonthlyProductId
              )
            }
          }
        })
        this.openModal('downgrade-subscription-modal')
      } else {
        this.openModal('downgrade-unavailable-modal')
      }
    },
    proceedToPayment() {
      if (this.newSubscription) {
        this.$store.dispatch('getAccountProfile')
        this.closeModal('downgrade-subscription-modal')
        this.$bvModal.show('new-subscription-payment')
      }
    },
    subscribeProduct({ resolve, reject }) {
      let subscriptionApparatuses = this.subscription.apparatuses.map(apparatus => apparatus.id)
      this.$store
        .dispatch('createPurchaseIntent', {
          productId: this.newSubscription.productId,
          organizationId: this.accountProfile.organizationId,
          apparatusIds: subscriptionApparatuses
        })
        .then(response => {
          this.$store
            .dispatch('updateSubscriptionProduct', {
              organizationId: this.accountProfile.organizationId,
              productId: this.newSubscription.productId
            })
            .then(response => {
              resolve(response)
              this.$bvModal.hide('new-subscription-payment')
              this.pollForSubscriptionDataAndRefresh()
            })
            .catch(error => {
              reject(this.$t('xPlanChangesFailed'))
            })
        })
        .catch(error => {
          reject(this.$t('xPlanChangesFailed'))
        })
    },
    async pollForSubscriptionDataAndRefresh() {
      const operationStart = Date.now()
      const delay = ms => new Promise(res => setTimeout(res, ms))
      let pollSubscription = null
      const response = await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .catch(() => {})
      pollSubscription = response.data?.subscriptions.find(
        subscription => subscription?.status === Constants.SubscriptionDetailPendingProductChange
      )
      if (pollSubscription) {
        this.$store.dispatch('displayToast', {
          title: this.$t('xSuccess'),
          message: this.$t('xPlanChangedSuccessfully'),
          variant: 'success'
        })
      }
      this.$bvModal.hide('new-subscription-payment')
    },
    returnToDowngrade() {
      this.$bvModal.hide('new-subscription-payment')
      this.openModal('downgrade-subscription-modal')
    },
    goToSelectedOption() {
      switch (this.managePlanType) {
        case ManagePlanTypes.CHANGE_PAYMENT_CADENCE:
          this.openModal('change-payment-cadence-modal')
          break
        case ManagePlanTypes.REMOVE_PRODUCTS:
          this.openModal('remove-apparatuses-selection-modal')
          break
        case ManagePlanTypes.DOWNGRADE_PLAN:
          this.openDowngradeModal()
          break
        case ManagePlanTypes.CANCEL_PLAN:
          this.openModal('cancel-subscription-modal')
          break
        default:
          break
      }
      this.closeManagePlanModal()
    }
  },
  async mounted() {
    if (!this.accountProfile) {
      await this.$store.dispatch('getAccountProfile')
    }
    if (!this.apparatuses) {
      await this.$store.dispatch('getApparatuses')
    }
  }
}
</script>

<style scoped lang="scss">
.radio-button-subtext {
  color: var(--carbon-600);
}
</style>
